import { clsx } from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { gateCopy } from '@/copy/gate';
import { copyText } from '@/lib/clipboard';
import { collectDiagnostics, diagnosticsText } from '@/lib/diagnostics';
import { detectInstalledApp, useInstallPrompt } from '@/lib/installPrompt';
import { formatCode, rememberPendingCode } from '@/lib/invite';
import { type GatePlatform, platformInfo } from '@/lib/platform';
import { Sheet } from '@/ui';
import { Button, Wordmark } from '../kit';
import { GateMock, type Scene } from './mockups';

/**
 * The install gate (pwa.md section 1, R12, R22). A render decision: the URL never changes and
 * nothing redirects. It renders for any browser tab, and when the server answers 428.
 * The stacked logo on the lilac ground inside the thick ink frame, then in order: the invite
 * code in big type with a Copy button (iOS does not share storage between Safari and the
 * home-screen app, so the code is the real hand-off), the in-app-browser explainer, the install
 * button where the browser offers one, and the per-platform steps with phone mockups.
 */

/** The mockup for each step, same order as gateCopy.platforms[...]. */
export const STEP_SCENES: Record<GatePlatform, readonly Scene[]> = {
  'ios-safari': ['ios-bar', 'ios-share', 'ios-add', 'home'],
  'ios-other': ['ios-bar', 'ios-share', 'ios-add', 'home'],
  'android-chrome': ['android-bar', 'android-menu', 'home'],
  'android-samsung': ['samsung-bar', 'samsung-menu', 'home'],
  'android-other': ['android-bar', 'android-menu', 'home'],
  'desktop-chromium': ['desktop-bar', 'desktop-dialog', 'desktop-window'],
  'desktop-safari': ['mac-menu', 'desktop-window'],
  'desktop-firefox': [],
  'desktop-other': [],
};

type Copied = 'code' | 'link' | 'diag' | 'failed' | null;

export function InstallGate({ code, serverSaid = false }: { code: string | null; serverSaid?: boolean }) {
  const info = platformInfo();
  // Inside another app's browser the steps are for the real browser the person is about to open.
  const stepsFor: GatePlatform = info.inAppBrowser
    ? info.platform === 'ios'
      ? 'ios-safari'
      : info.platform === 'android'
        ? 'android-chrome'
        : info.gate
    : info.gate;
  const steps = gateCopy.platforms[stepsFor];
  const scenes = STEP_SCENES[stepsFor];
  const event = useInstallPrompt((s) => s.event);
  const installed = useInstallPrompt((s) => s.installed);
  const outcome = useInstallPrompt((s) => s.outcome);
  const alreadyInstalled = useInstallPrompt((s) => s.alreadyInstalled);
  const promptInstall = useInstallPrompt((s) => s.promptInstall);
  const [copied, setCopied] = useState<Copied>(null);
  const [diag, setDiag] = useState(false);
  const diagnostics = useMemo(() => (diag ? collectDiagnostics() : {}), [diag]);

  useEffect(() => {
    if (code) rememberPendingCode(code);
    document.title = 'Install TTS Basketball';
  }, [code]);

  useEffect(() => {
    void detectInstalledApp();
  }, []);

  const copy = async (what: 'code' | 'link' | 'diag', text: string) => {
    setCopied((await copyText(text)) ? what : 'failed');
  };

  const inAppSteps =
    info.platform === 'ios'
      ? gateCopy.inApp.stepsIos
      : info.platform === 'android'
        ? gateCopy.inApp.stepsAndroid
        : gateCopy.inApp.stepsOther;

  return (
    <main className="tts-gate relative min-h-dvh overflow-x-hidden bg-lilac text-ink">
      <div className="tts-frame" aria-hidden="true" />
      <div className="tts-pt-safe tts-pb-safe">
        <div className="mx-auto flex max-w-md flex-col items-stretch gap-6 px-7 pt-12 pb-14">
          <div className="flex justify-center pt-4">
            <Wordmark variant="stack" width="min(64vw, 250px)" label="TTS Basketball" />
          </div>

          <header className="flex flex-col items-start gap-3">
            <h1 className="tts-plate tts-display bg-butter text-[1.7rem]">
              <span>{gateCopy.title}</span>
            </h1>
            <p className="text-[1.05rem] font-semibold leading-snug">{gateCopy.body}</p>
            {serverSaid && <p className="tts-card-pale px-3 py-2 text-sm font-semibold">{gateCopy.serverGate}</p>}
          </header>

          {code && (
            <section className="tts-card flex flex-col items-center gap-3 px-4 py-5 text-center" aria-live="polite">
              <p className="text-sm font-bold uppercase tracking-wider">{gateCopy.codeLabel}</p>
              <p className="tts-number select-all text-[3.1rem] leading-none tracking-wide" data-testid="gate-code">
                {formatCode(code)}
              </p>
              <Button variant="primary" size="lg" icon="copy" onClick={() => copy('code', formatCode(code))}>
                {copied === 'code' ? gateCopy.copied : gateCopy.copy}
              </Button>
              <p className="text-sm font-medium">{copied === 'failed' ? gateCopy.copyFailed : gateCopy.codeNote}</p>
            </section>
          )}

          {alreadyInstalled && (
            <p className="tts-card bg-mint px-4 py-3 font-bold" data-testid="gate-already-installed">
              {gateCopy.alreadyHave}
            </p>
          )}

          {info.inAppBrowser && (
            <section className="tts-card-pale flex flex-col gap-3 px-4 py-4" data-testid="gate-inapp">
              <h2 className="tts-display text-xl">{gateCopy.inApp.title}</h2>
              <p className="font-medium leading-snug">{gateCopy.inApp.body}</p>
              <ol className="flex flex-col gap-2">
                {inAppSteps.map((s, i) => (
                  <li key={s} className="flex items-start gap-3">
                    <StepNumber n={i + 1} />
                    <span className="pt-1 font-medium leading-snug">{s}</span>
                  </li>
                ))}
              </ol>
              <Button icon="share" onClick={() => copy('link', window.location.href)}>
                {copied === 'link' ? gateCopy.linkCopied : gateCopy.copyLink}
              </Button>
            </section>
          )}

          {event && !installed && (
            <div className="flex flex-col gap-2">
              <Button variant="go" size="xl" icon="plus" block onClick={() => void promptInstall()}>
                {gateCopy.installButton}
              </Button>
              {outcome === 'dismissed' && <p className="text-sm font-semibold">{gateCopy.installDismissed}</p>}
            </div>
          )}
          {!event && outcome === 'dismissed' && <p className="text-sm font-semibold">{gateCopy.installDismissed}</p>}
          {installed && (
            <p className="tts-card bg-mint px-4 py-3 font-bold" data-testid="gate-installed">
              {info.platform === 'desktop' ? gateCopy.installedDesktop : gateCopy.installedAndroid}
            </p>
          )}

          <section className="tts-card relative mt-2 px-4 pt-7 pb-4">
            <h2 className="tts-plate tts-display absolute -top-4 left-3 bg-sky text-base">
              <span>{info.inAppBrowser ? gateCopy.inApp.after : gateCopy.steps}</span>
            </h2>
            <ol className="flex flex-col gap-4">
              {steps.map((s, i) => {
                const scene = scenes[i];
                return (
                  <li key={s} className="flex items-center gap-3" data-testid="gate-step">
                    {scene ? (
                      <div className="relative shrink-0">
                        <GateMock
                          scene={scene}
                          width={scene.startsWith('desktop') || scene === 'mac-menu' ? 112 : 84}
                        />
                        <span className="absolute -top-1 -left-1">
                          <StepNumber n={i + 1} />
                        </span>
                      </div>
                    ) : (
                      <StepNumber n={i + 1} />
                    )}
                    <span className="font-medium leading-snug">{s}</span>
                  </li>
                );
              })}
            </ol>
          </section>

          {info.platform === 'ios' && !info.inAppBrowser && (
            <section className="tts-card-pale flex flex-col gap-3 px-4 py-3">
              <p className="text-sm font-semibold">{gateCopy.inAppBrowser}</p>
              <Button icon="share" onClick={() => copy('link', window.location.href)}>
                {copied === 'link' ? gateCopy.linkCopied : gateCopy.copyLink}
              </Button>
            </section>
          )}

          <footer className="flex flex-col gap-2 text-sm font-medium">
            <p>{gateCopy.softGate}</p>
            <button
              type="button"
              className="min-h-11 self-start font-bold underline underline-offset-4"
              onClick={() => setDiag(true)}
            >
              {gateCopy.already}
            </button>
          </footer>
        </div>
      </div>

      <Sheet open={diag} onClose={() => setDiag(false)} title={gateCopy.diagTitle} closeLabel={gateCopy.close}>
        <div className="flex flex-col gap-3 pb-2">
          <p className="text-sm font-medium">{gateCopy.diagIntro}</p>
          <dl
            className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 break-all font-mono text-xs"
            data-testid="gate-diagnostics"
          >
            {Object.entries(diagnostics).map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="font-bold">{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
          <Button icon="copy" onClick={() => copy('diag', diagnosticsText(diagnostics))}>
            {copied === 'diag' ? gateCopy.diagCopied : gateCopy.diagCopy}
          </Button>
        </div>
      </Sheet>
    </main>
  );
}

function StepNumber({ n, className }: { n: number; className?: string }) {
  return (
    <span
      className={clsx(
        'tts-display grid size-8 shrink-0 place-items-center rounded-chip border-3 border-ink bg-lavender text-base',
        className,
      )}
    >
      {n}
    </span>
  );
}
